/**
 * Qualified Field ID Utilities
 *
 * When the same table is joined multiple times with different aliases,
 * fields share identical UUIDs. These utilities create composite keys
 * in the format "alias:fieldId" to disambiguate them.
 *
 * Backward compatible: bare UUIDs (no ":") still work via fallback resolution.
 *
 * @module @loader/shared-types/utils/qualifiedFieldId
 */

/** Separator between alias and field ID in qualified field IDs */
const QUALIFIED_SEPARATOR = ':';

/**
 * Create a qualified field ID: "alias:fieldId"
 *
 * @param alias - Table alias
 * @param fieldId - Field UUID
 * @returns Qualified field ID string
 *
 * @example
 * ```ts
 * qualifyFieldId('sales_14', '9b8f148d-a8f0-4ddd-ae43-cc610803ec64')
 * // 'sales_14:9b8f148d-a8f0-4ddd-ae43-cc610803ec64'
 * ```
 */
export function qualifyFieldId(alias: string, fieldId: string): string {
  return `${alias}${QUALIFIED_SEPARATOR}${fieldId}`;
}

/**
 * Parse a potentially qualified field ID
 *
 * @param qualifiedId - Either "alias:fieldId" or bare "fieldId"
 * @returns Object with alias (null if bare) and fieldId
 *
 * @example
 * ```ts
 * parseFieldId('sales_14:9b8f148d-...')
 * // { alias: 'sales_14', fieldId: '9b8f148d-...' }
 *
 * parseFieldId('9b8f148d-...')
 * // { alias: null, fieldId: '9b8f148d-...' }
 * ```
 */
export function parseFieldId(qualifiedId: string): { alias: string | null; fieldId: string } {
  const sepIndex = qualifiedId.indexOf(QUALIFIED_SEPARATOR);
  if (sepIndex === -1) {
    return { alias: null, fieldId: qualifiedId };
  }
  return {
    alias: qualifiedId.substring(0, sepIndex),
    fieldId: qualifiedId.substring(sepIndex + 1),
  };
}

/**
 * Check if a field ID is qualified (contains alias prefix)
 *
 * @param id - Field ID string to check
 * @returns True if the ID contains an alias prefix
 */
export function isQualifiedFieldId(id: string): boolean {
  return id.includes(QUALIFIED_SEPARATOR);
}
